import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { IconType } from 'react-icons';
import {
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';

export interface SidebarNavItem {
  label: string;
  description?: string;
  path: string;
  icon: IconType;
}

export interface SidebarNavSection {
  id: string;
  label: string;
  icon: IconType;
  description?: string;
  items: SidebarNavItem[];
}

interface SidebarDrawerProps {
  sections: SidebarNavSection[];
  collapsed: boolean;
  onToggle: () => void;
}

export const SidebarDrawer: React.FC<SidebarDrawerProps> = ({
  sections,
  collapsed,
  onToggle,
}) => {
  const firstSectionId = useMemo<string | undefined>(
    () => sections[0]?.id,
    [sections],
  );
  const [openSectionId, setOpenSectionId] = useState<string | undefined>(
    firstSectionId,
  );

  useEffect(() => {
    if (!openSectionId && firstSectionId) {
      setOpenSectionId(firstSectionId);
      return;
    }

    const exists = sections.some((section) => section.id === openSectionId);
    if (!exists) {
      setOpenSectionId(firstSectionId);
    }
  }, [firstSectionId, openSectionId, sections]);

  const handleSectionToggle = (sectionId: string) => {
    if (collapsed) {
      onToggle();
      setOpenSectionId(sectionId);
      return;
    }

    setOpenSectionId((current) =>
      current === sectionId ? undefined : sectionId,
    );
  };

  return (
    <aside
      className={`app-drawer ${collapsed ? 'app-drawer--collapsed' : ''}`}
      aria-label="Menu administrativo"
    >
      <div className="app-drawer__header">
        {!collapsed && <span className="app-drawer__title">Admin Area</span>}
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={onToggle}
          className="app-drawer__toggle"
          aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          {collapsed ? <FiChevronRight /> : <FiChevronLeft />}
        </Button>
      </div>

      <nav className="app-drawer__nav">
        {sections.map((section) => {
          const SectionIcon = section.icon;
          const expanded = openSectionId === section.id && !collapsed;

          return (
            <div
              className={`app-drawer__section ${
                expanded ? 'app-drawer__section--open' : ''
              }`}
              key={section.id}
            >
              <button
                type="button"
                className={`app-drawer__section-toggle ${
                  collapsed ? 'app-drawer__section-toggle--icon' : ''
                }`}
                onClick={() => handleSectionToggle(section.id)}
                title={collapsed ? section.label : undefined}
              >
                <span className="app-drawer__icon">
                  <SectionIcon size={20} />
                </span>
                {!collapsed && (
                  <span className="app-drawer__content">
                    <span className="app-drawer__label">{section.label}</span>
                    {section.description && (
                      <small className="app-drawer__description">
                        {section.description}
                      </small>
                    )}
                  </span>
                )}
                {!collapsed && (
                  <span
                    className={`app-drawer__caret ${
                      expanded ? 'is-open' : ''
                    }`}
                  >
                    <FiChevronDown size={16} />
                  </span>
                )}
              </button>

              {expanded && (
                <div className="app-drawer__section-content">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        end
                        className={({ isActive }) =>
                          [
                            'app-drawer__link',
                            'app-drawer__link--nested',
                            isActive ? 'app-drawer__link--active' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')
                        }
                      >
                        <span className="app-drawer__icon">
                          <Icon size={18} />
                        </span>
                        <span className="app-drawer__content">
                          <span className="app-drawer__label">
                            {item.label}
                          </span>
                          {item.description && (
                            <small className="app-drawer__description">
                              {item.description}
                            </small>
                          )}
                        </span>
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
};
